import { useMemo, useState } from 'react'
import { Info, PackageMinus, PackagePlus } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'
import { InventoryInTransactionForm } from '../components/InventoryInTransactionForm'
import { InventoryOutTransactionForm } from '../components/InventoryOutTransactionForm'
import { TransactionDetails } from '../components/TransactionDetails'
import { TransactionCard } from '../components/TransactionCard'
import type { transactionFilterSchema } from '@/schemas/filterSchemas'
import { usePagination } from '@/hooks/usePagination'
import { useCleanQueryStates } from '@/hooks/useCleanQueryStates'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/timeZoneUtils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Separator } from '@/components/ui/separator'
import { useIsMobile } from '@/hooks'

export default function Transactions() {
  const isMobile = useIsMobile()

  const transactions = useInventoryStore((state) => state.transactions)
  const categories = useInventoryStore((state) => state.categories)
  const churches = useChurchesStore((state) => state.churches)

  const [activeTab, setActiveTab] = useState(churches[0]?._id || '')
  const [open, setOpen] = useState({ in: false, out: false, details: false })
  const [transactionId, setTransactionId] = useState<string | null>(null)

  const [query, setQuery] = useCleanQueryStates<typeof transactionFilterSchema>(
    '/authed/inventory/transactions',
  )

  const churchTransactions = useMemo(
    () =>
      transactions
        .filter((t) => t.churchId === activeTab)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [transactions, activeTab],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: churchTransactions,
    pageSize: 10,
  })

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <ListPageHeader
          type="transactions"
          searchValue={query.search}
          onSearchChange={(value) => {
            setQuery({ search: value })
            goToFirstPage()
          }}
          initialFilters={query}
          onFilter={(filters) => {
            setQuery(filters)
            goToFirstPage()
          }}
          isFormOpen={false}
          onFormOpen={() => {}}
          onFormClose={() => {}}
          showAddButton={false}
          customActions={
            <>
              <Button onClick={() => setOpen((o) => ({ ...o, in: true }))}>
                <PackagePlus /> Add Inventory
              </Button>
              <Button onClick={() => setOpen((o) => ({ ...o, out: true }))}>
                <PackageMinus /> Remove Inventory
              </Button>
            </>
          }
        />

        {/* Mobile Action Buttons */}
        <div className="flex lg:hidden gap-2">
          <Button
            onClick={() => setOpen((o) => ({ ...o, in: true }))}
            className="flex-1"
          >
            <PackagePlus /> Add Inventory
          </Button>
          <Button
            onClick={() => setOpen((o) => ({ ...o, out: true }))}
            className="flex-1"
          >
            <PackageMinus /> Remove Inventory
          </Button>
        </div>
      </div>

      {!isMobile && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex justify-between">
            {churches.map((church) => (
              <TabsTrigger key={church._id} value={church._id}>
                {church.name}, {church.address.province}
              </TabsTrigger>
            ))}
          </TabsList>
          {churches.map((church) => (
            <TabsContent key={church._id} value={church._id}>
              {/* Content is the same, but filtered by church */}
              <>
                <div className="overflow-hidden rounded-md border bg-white">
                  <Table>
                    <THead>
                      <TR>
                        <TH>Date</TH>
                        <TH>Type</TH>
                        <TH>Category</TH>
                        <TH>Item</TH>
                        <TH>Unit</TH>
                        <TH>Quantity</TH>
                        <TH className="text-right">Actions</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {rows.map((transaction) => {
                        const category = categories.find(
                          (c) => c._id === transaction.categoryId,
                        )
                        return (
                          <TR key={transaction._id}>
                            <TD>
                              {formatDate(
                                transaction.createdAt,
                                'MMM DD, YYYY',
                              )}
                            </TD>
                            <TD>
                              {transaction.type === 'IN' ? (
                                <Badge
                                  variant="outline"
                                  className="inline-flex items-center gap-2 text-green-600 border-green-300 bg-green-100"
                                >
                                  <PackagePlus className="h-3 w-3" />
                                  <span>In</span>
                                </Badge>
                              ) : (
                                <Badge
                                  variant="default"
                                  className="inline-flex items-center gap-2 text-red-600 border-red-300 bg-red-100"
                                >
                                  <PackageMinus className="h-3 w-3" />
                                  Out
                                </Badge>
                              )}
                            </TD>
                            <TD>{category?.name}</TD>
                            <TD>{`${transaction.item}${transaction.brand ? ` (${transaction.brand})` : ''}`}</TD>
                            <TD>{transaction.unit}</TD>
                            <TD>{transaction.quantity}</TD>
                            <TD className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setTransactionId(transaction._id)
                                    setOpen((o) => ({ ...o, details: true }))
                                  }}
                                >
                                  <Info className="h-4 w-4" /> Details
                                </Button>
                              </div>
                            </TD>
                          </TR>
                        )
                      })}
                      {rows.length === 0 && (
                        <TR>
                          <TD
                            className="p-6 text-center text-neutral-500"
                            colSpan={7}
                          >
                            No transactions found
                          </TD>
                        </TR>
                      )}
                    </TBody>
                  </Table>
                </div>
                <PaginationWrapper
                  pageCount={pageCount}
                  currentPage={page}
                  onPageChange={setPage}
                />
              </>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Mobile Cards */}
      {isMobile && (
        <div className="space-y-3 pb-4">
          <AutoCompleteSelect
            field={
              {
                state: { value: activeTab },
                handleChange: (val: string) => setActiveTab(val),
              } as any
            }
            options={churches.map((church) => ({
              value: church._id,
              label: church.name,
            }))}
          />
          <Separator />
          <ScrollArea className="h-[calc(65vh)]">
            <div className="space-y-3 pb-4">
              {churchTransactions.length === 0 ? (
                <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
                  No transactions found
                </div>
              ) : (
                churchTransactions.map((transaction) => {
                  return (
                    <TransactionCard
                      key={transaction._id}
                      transaction={transaction}
                      onDetail={(id) => {
                        setTransactionId(id)
                        setOpen((o) => ({ ...o, details: true }))
                      }}
                    />
                  )
                })
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      <TransactionDetails
        transactionId={transactionId}
        open={open.details}
        onClose={() => {
          setOpen((o) => ({ ...o, details: false }))
          setTransactionId(null)
        }}
      />

      <InventoryInTransactionForm
        open={open.in}
        onClose={() => setOpen((o) => ({ ...o, in: false }))}
      />
      <InventoryOutTransactionForm
        open={open.out}
        onClose={() => setOpen((o) => ({ ...o, out: false }))}
      />
    </div>
  )
}
